import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import * as Icons from "lucide-react";
import Seo from "../components/Seo";
import InquiryForm from "../components/InquiryForm";
import { useSite } from "../components/SiteContext";
import {
  EditorialCard, Empty, FaqList, Grid, Loading, OverlayCard, RichText, Reviews,
  Section, SectionHead,
} from "../components/shared";
import { getHomepage, destUrl, inr } from "../lib/api";
import { fadeUp, stagger, containerViewport } from "../lib/motion";

const HOME_KEYWORDS = ["luxury travel india", "bespoke holidays", "corporate travel", "visa services",
          "destination management india"];

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Sparkle;
  return <C className={className} />;
};

export default function Home() {
  const { settings } = useSite();
  const [data, setData] = useState(null);
  const [videoOk, setVideoOk] = useState(true);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 110]);

  useEffect(() => {
    getHomepage().then(setData).catch(() => setData({ sections: [] }));
  }, []);

  if (!data) return <Loading label="Preparing your journey" />;

  const s = { ...settings, ...(data.settings || {}) };
  const section = (key) => (data.sections || []).find((x) => x.slug === key && x.published);

  return (
    <>
      <Seo
        title={s.default_meta_title}
        description={s.default_meta_description}
        image={s.default_og_image}
        path="/"
        keywords={HOME_KEYWORDS}
      />

      {/* HERO */}
      <section
        data-testid="home-hero"
        className="grain relative isolate flex min-h-[100svh] items-center overflow-hidden bg-[var(--navy)]"
      >
        <motion.div style={{ y: heroY }} className="absolute inset-0 -z-10 h-[118%]">
          {s.hero_poster && (
            <img src={s.hero_poster} alt="" className="absolute inset-0 img-cover" loading="eager" />
          )}
          {s.hero_video && videoOk && (
            <video
              autoPlay
              muted
              loop
              playsInline
              poster={s.hero_poster}
              onError={() => setVideoOk(false)}
              data-testid="hero-video"
              className="absolute inset-0 img-cover"
            >
              <source src={s.hero_video} type="video/mp4" />
            </video>
          )}
        </motion.div>
        <div className="absolute inset-0 -z-10 hero-scrim" />

        <div className="az-container relative w-full py-32">
          <motion.div variants={stagger} initial="hidden" animate="show" className="max-w-3xl">
            <motion.p
              variants={fadeUp}
              className="mb-6 text-[0.7rem] font-semibold uppercase tracking-[0.3em] text-[#E4CFA9]"
            >
              {s.tagline}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              data-testid="hero-headline"
              className="font-display text-[2.6rem] leading-[1.04] text-white sm:text-6xl lg:text-[4.6rem]"
            >
              {s.hero_headline || "Every Journey, Beautifully Curated."}
            </motion.h1>
            <motion.ul
              variants={fadeUp}
              data-testid="hero-subheadline"
              className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/80 sm:text-base"
            >
              {(s.hero_subheadline || []).map((item, i) => (
                <li key={item} className="flex items-center gap-5">
                  {i > 0 && <span className="h-1 w-1 rounded-full bg-[var(--brass)]" />}
                  {item}
                </li>
              ))}
            </motion.ul>
            <motion.div variants={fadeUp} className="mt-10 flex flex-wrap gap-3">
              <Link
                to={s.hero_cta_link || "/plan-my-journey"}
                data-testid="hero-primary-cta"
                className="rounded-xl bg-[var(--brass)] px-7 py-4 text-sm font-semibold text-white shadow-[0_14px_40px_rgba(14,30,42,0.3)] transition-colors hover:bg-[var(--brass-2)] active:scale-[0.98]"
              >
                {s.hero_cta_label || "Plan My Journey"}
              </Link>
              <Link
                to={s.hero_cta2_link || "/corporate"}
                data-testid="hero-secondary-cta"
                className="rounded-xl border border-white/35 bg-white/10 px-7 py-4 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20"
              >
                {s.hero_cta2_label || "Corporate Travel"}
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* TRUST BAR */}
      <div data-testid="trust-bar" className="border-b border-[var(--line)] bg-[var(--surface)]">
        <div className="az-container flex flex-wrap items-center justify-center gap-x-10 gap-y-4 py-6">
          {(s.trust_items || []).map((t) => (
            <div key={t.label} className="flex items-center gap-2.5 text-xs text-[var(--muted-ink)]">
              <Icon name={t.icon} className="h-4 w-4 text-[var(--brass)]" />
              <span className="font-medium">{t.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* CMS-editable introduction and contextual journey links. */}
      {section("seo-introduction") && (
        <Section testId="home-seo-introduction">
          <div className="max-w-3xl">
            <SectionHead title={section("seo-introduction").title} subtitle={section("seo-introduction").subtitle} />
            <RichText text={section("seo-introduction").body} />
          </div>
        </Section>
      )}

      {/* CATEGORIES */}
      {section("categories") && data.categories?.length > 0 && (
        <Section wash testId="home-categories">
          <SectionHead
            eyebrow="What we do"
            title={section("categories").title}
            subtitle={section("categories").subtitle}
            cta={section("categories").cta_link}
            ctaLabel={section("categories").cta_label}
          />
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={containerViewport}
            className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 lg:gap-4"
          >
            {data.categories.map((c) => (
              <motion.div key={c.slug} variants={fadeUp}>
                <Link
                  to={c.link || "/contact"}
                  data-testid={`category-card-${c.slug}`}
                  className="group flex h-full flex-col justify-between rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-[var(--brass)] hover:shadow-[0_14px_36px_rgba(14,30,42,0.1)]"
                >
                  <Icon name={c.icon} className="mb-6 h-5 w-5 text-[var(--brass)]" />
                  <div>
                    <h3 className="font-display text-lg leading-tight text-[var(--ink)]">{c.title}</h3>
                    <p className="mt-1.5 text-xs leading-relaxed text-[var(--muted-ink)] line-clamp-2">
                      {c.description}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </Section>
      )}

      {/* SIGNATURE JOURNEYS */}
      {data.journeys?.length > 0 && (
        <Section testId="home-journeys">
          <SectionHead
            eyebrow="Our own designs"
            title={section("journeys")?.title || "Signature Journeys"}
            subtitle={section("journeys")?.subtitle}
            cta="/journeys"
            ctaLabel="See all journeys"
          />
          <Grid cols={3}>
            {data.journeys.slice(0, 6).map((j) => (
              <OverlayCard
                key={j.slug}
                to={`/journeys/${j.slug}`}
                image={j.card_image || j.hero_image}
                title={j.title}
                meta={`${j.nights} nights · from ${inr(j.price_inr)}`}
                subtitle={j.tagline}
                testId={`journey-card-${j.slug}`}
              />
            ))}
          </Grid>
        </Section>
      )}

      {/* DESTINATIONS */}
      {data.destinations?.length > 0 && (
        <Section wash testId="home-destinations">
          <SectionHead
            eyebrow="Destinations"
            title={section("destinations")?.title || "Where we know best"}
            subtitle={section("destinations")?.subtitle}
            cta="/international"
            ctaLabel="Explore all destinations"
          />
          <Grid cols={3}>
            {data.destinations.slice(0, 6).map((d) => (
              <EditorialCard
                key={d.slug}
                to={destUrl(d)}
                image={d.card_image || d.hero_image}
                title={d.title}
                meta={`${d.region === "india" ? "India" : d.country} · ${d.duration_note}`}
                subtitle={d.tagline}
                testId={`destination-card-${d.slug}`}
              />
            ))}
          </Grid>
        </Section>
      )}

      {/* AI PLANNER TEASER */}
      {section("planner") && (
        <Section testId="home-planner">
          <div className="overflow-hidden rounded-3xl border border-[var(--line)] bg-[var(--surface)]">
            <div className="grid lg:grid-cols-2">
              <div className="flex flex-col justify-center p-8 sm:p-12 lg:p-16">
                <p className="eyebrow mb-4">AI Trip Planner</p>
                <h2 className="font-display text-3xl leading-tight text-[var(--ink)] sm:text-4xl">
                  {section("planner").title}
                </h2>
                <p className="mt-4 text-[0.95rem] leading-relaxed text-[var(--muted-ink)]">
                  {section("planner").subtitle}
                </p>
                <Link
                  to={section("planner").cta_link || "/plan-my-journey"}
                  data-testid="home-planner-cta"
                  className="mt-8 inline-block w-fit rounded-xl bg-[var(--brass)] px-7 py-4 text-sm font-semibold text-white transition-colors hover:bg-[var(--brass-2)]"
                >
                  {section("planner").cta_label || "Try the trip planner"}
                </Link>
              </div>
              <div className="az-media min-h-[280px] lg:min-h-[440px]">
                {section("planner").image && (
                  <img src={section("planner").image} alt="" loading="lazy" className="img-cover" />
                )}
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* EXPERIENCES */}
      {data.experiences?.length > 0 && (
        <Section wash testId="home-experiences">
          <SectionHead
            eyebrow="Destination management"
            title={section("experiences")?.title || "Curated Experiences"}
            subtitle={section("experiences")?.subtitle}
            cta="/experiences"
            ctaLabel="Browse experiences"
          />
          <Grid cols={4}>
            {data.experiences.slice(0, 4).map((e) => (
              <EditorialCard
                key={e.slug}
                to={`/experiences/${e.slug}`}
                image={e.card_image || e.hero_image}
                title={e.title}
                meta={`${e.city} · ${e.duration}`}
                subtitle={e.tagline}
                testId={`experience-card-${e.slug}`}
              />
            ))}
          </Grid>
        </Section>
      )}

      {/* TRAVEL STYLES */}
      {data.travel_styles?.length > 0 && (
        <Section testId="home-styles">
          <SectionHead
            eyebrow="Travel styles"
            title={section("styles")?.title || "Travel, your way"}
            subtitle={section("styles")?.subtitle}
            cta="/travel-styles"
            ctaLabel="All travel styles"
          />
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={containerViewport}
            className="flex flex-wrap gap-3"
          >
            {data.travel_styles.map((st) => (
              <motion.div key={st.slug} variants={fadeUp}>
                <Link
                  to={`/travel-styles/${st.slug}`}
                  data-testid={`style-pill-${st.slug}`}
                  className="flex items-center gap-2.5 rounded-full border border-[var(--line-strong)] bg-[var(--surface)] px-5 py-3 text-sm font-medium text-[var(--ink-2)] transition-colors hover:border-[var(--brass)] hover:text-[var(--brass-2)]"
                >
                  <Icon name={st.icon} className="h-4 w-4 text-[var(--brass)]" />
                  {st.title}
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </Section>
      )}

      {/* SEASONAL */}
      {data.seasonal_collections?.length > 0 && (
        <Section wash testId="home-seasonal">
          <SectionHead
            eyebrow="Timing matters"
            title={section("seasonal")?.title || "Seasonal Collections"}
            subtitle={section("seasonal")?.subtitle}
            cta="/collections"
            ctaLabel="See collections"
          />
          <Grid cols={4}>
            {data.seasonal_collections.slice(0, 4).map((c) => (
              <OverlayCard
                key={c.slug}
                to={`/collections/${c.slug}`}
                image={c.card_image || c.hero_image}
                title={c.title}
                meta={c.window}
                testId={`seasonal-card-${c.slug}`}
              />
            ))}
          </Grid>
        </Section>
      )}

      {/* CORPORATE */}
      {section("corporate") && (
        <Section testId="home-corporate">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div className="az-media overflow-hidden rounded-3xl" style={{ aspectRatio: "4/3" }}>
              {section("corporate").image && (
                <img src={section("corporate").image} alt="" loading="lazy" className="img-cover" />
              )}
            </div>
            <div>
              <p className="eyebrow mb-4">Corporate &amp; MICE</p>
              <h2 className="font-display text-3xl leading-tight text-[var(--ink)] sm:text-4xl">
                {section("corporate").title}
              </h2>
              <p className="mt-4 text-[0.95rem] leading-relaxed text-[var(--muted-ink)]">
                {section("corporate").subtitle}
              </p>
              {data.clients?.length > 0 && (
                <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
                  {data.clients.map((c) => (
                    <span key={c.slug} className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--muted-ink)]">
                      {c.title}
                    </span>
                  ))}
                </div>
              )}
              <Link
                to="/corporate"
                data-testid="home-corporate-cta"
                className="mt-8 inline-block rounded-xl border border-[var(--line-strong)] bg-[var(--surface)] px-7 py-4 text-sm font-semibold text-[var(--ink)] transition-colors hover:border-[var(--brass)] hover:text-[var(--brass-2)]"
              >
                {section("corporate").cta_label || "Corporate Travel"}
              </Link>
            </div>
          </div>
        </Section>
      )}

      {/* TESTIMONIALS */}
      {data.testimonials?.length > 0 && (
        <Section wash testId="home-testimonials">
          <SectionHead
            eyebrow="Guest stories"
            title={section("testimonials")?.title || "In their words"}
            subtitle={section("testimonials")?.subtitle}
          />
          <Reviews reviews={data.testimonials.slice(0, 6)} testId="home-testimonial" />
        </Section>
      )}

      {/* JOURNAL */}
      {data.blogs?.length > 0 && (
        <Section testId="home-journal">
          <SectionHead
            eyebrow="The Journal"
            title={section("journal")?.title || "The Journal"}
            subtitle={section("journal")?.subtitle}
            cta="/blog"
            ctaLabel="Read the journal"
          />
          <Grid cols={3}>
            {data.blogs.slice(0, 3).map((b) => (
              <EditorialCard
                key={b.slug}
                to={`/blog/${b.slug}`}
                image={b.hero_image}
                title={b.title}
                meta={`${b.category} · ${b.read_time}`}
                subtitle={b.excerpt}
                testId={`blog-card-${b.slug}`}
              />
            ))}
          </Grid>
        </Section>
      )}

      {/* FAQ + FINAL CTA */}
      <Section wash testId="home-faq">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-20">
          <div>
            <p className="eyebrow mb-3">Before you ask</p>
            <h2 className="font-display text-3xl leading-tight text-[var(--ink)] sm:text-4xl">
              Frequently asked
            </h2>
            <div className="mt-8">
              {data.faqs?.length ? (
                <FaqList faqs={data.faqs} testId="home-faq-accordion" />
              ) : (
                <Empty title="Nothing here yet" />
              )}
            </div>
          </div>
          <div id="enquire">
            <InquiryForm
              source="contact"
              title={section("cta")?.title || "Let's design something worth remembering"}
              subtitle={section("cta")?.subtitle}
            />
          </div>
        </div>
      </Section>
    </>
  );
}
